import Component from 'flarum/Component';
import username from 'flarum/helpers/username';
import fullTime from 'flarum/helpers/fullTime';
import classList from 'flarum/utils/classList';
import humanTime from 'flarum/utils/humanTime';
import extractText from 'flarum/utils/extractText';
import ItemList from 'flarum/utils/ItemList';
import SubtreeRetainer from 'flarum/utils/SubtreeRetainer';
import Avatar from 'flarum/common/components/Avatar';
import Dropdown from 'flarum/components/Dropdown';
import Button from 'flarum/components/Button';
import Separator from 'flarum/components/Separator';
import Link from 'flarum/components/Link';
export default class ChatMessage extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.labels = [];
    this.model = this.attrs.model;
    // Never assign onto `model.content` — F2 Message defines content as
    // Model.attribute('content'), so bare `.content` is the getter function
    // and Mithril would render its source text.
    this.displayContent = this.model.message();
    this.initLabels();
    this.subtree = new SubtreeRetainer(() => this.model.freshness, () => this.model.user().freshness, () => app.chat.getCurrentChat(),
    // Reactive attrs
    () => this.displayContent || this.model.message(), () => this.model.isDeletedForever, () => this.model.isTimedOut, () => this.model.isEditing, () => this.model.isNeedToFlash);
  }
  modelEvent(name) {
    const viewportState = app.chat.getViewportState(this.model.chat());
    viewportState.onChatMessageClicked(name, this.model);
    app.chat.onChatMessageClicked(name, this.model);
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    this.model = this.attrs.model;
    return this.subtree.needsRebuild();
  }

  /**
   * Canonical author for presentation. Avatar, nickname, and profile link must
   * all derive from this same object (FORUM-MESSAGING-006UI).
   */
  authorForPresentation() {
    const messageAuthor = this.model.user();
    const own = this.isOwnMessage();
    if (own && this.isMessagesV2() && app.session.user) {
      return app.session.user;
    }
    return messageAuthor;
  }
  content() {
    const author = this.authorForPresentation();

    // FORUM-MESSAGING-008UI: V2 grouped rows are content-only (identity lives on ChatMessageGroup).
    if (this.isMessagesV2() && this.attrs.grouped) {
      return this.v2GroupedContent();
    }

    // Fallback if a V2 message is rendered outside a group (should be rare).
    if (this.isMessagesV2() && this.isOwnMessage()) {
      return this.v2OwnContent(author);
    }
    return this.legacyContent(author);
  }

  /**
   * Messages V2 body inside ChatMessageGroup — no avatar/name/time.
   * Staff moderation menu is attached per message when authorized.
   */
  v2GroupedContent() {
    const canModerate = this.canModerateMessage();
    return m("div", {
      className: "ChatMessage-content ChatMessage-content--grouped"
    }, m("div", {
      className: "labels"
    }, this.labels.map(label => label.condition() ? label.component() : null)), m("div", {
      className: "ChatMessage-groupedRow"
    }, this.messageBody(), canModerate && this.model.id() && !this.model.deleted_by() ? this.moderationDropdown() : null));
  }

  /**
   * Canonical Live moderation gate. Uses forum-serialized moderate permission
   * (administrators inherit via Flarum can(); do not invent username heuristics).
   */
  canModerateMessage() {
    return !!app.forum.attribute('flatrate-live-chat.permissions.moderate');
  }

  /**
   * Narrow staff-only menu: Remove message → existing DELETE path (dropdownDelete).
   * Do not route staff removal through the inherited hide mutation.
   */
  moderationDropdown() {
    const label = app.translator.trans('flatrate-live-chat.forum.chat.message.actions.remove');
    return m("div", {
      className: "ChatMessage-moderation"
    }, m(Dropdown, {
      buttonClassName: "Button Button--icon Button--flat ChatMessage-moderationToggle",
      menuClassName: "Dropdown-menu Dropdown-menu--top Dropdown-menu--bottom Dropdown-menu--left Dropdown-menu--right",
      icon: "fas fa-ellipsis-h",
      label: label,
      accessibleToggleLabel: extractText(label)
    }, m(Button, {
      onclick: this.modelEvent.bind(this, 'dropdownDelete'),
      icon: "fas fa-trash-alt"
    }, label)));
  }

  /**
   * Legacy V2 own-message DOM kept as fallback when not wrapped by ChatMessageGroup.
   * Avatar participates in layout (grid); do not rely on flex row-reverse.
   */
  v2OwnContent(author) {
    return m("div", {
      className: "ChatMessage-row ChatMessage-row--own"
    }, m("div", {
      className: "ChatMessage-content"
    }, m("div", {
      className: "ChatMessage-meta"
    }, m("a", {
      className: "name",
      onclick: this.modelEvent.bind(this, 'insertMention')
    }, extractText(username(author)) + ':'), m("div", {
      className: "labels"
    }, this.labels.map(label => label.condition() ? label.component() : null)), this.model.id() ? m("a", {
      className: "timestamp",
      title: extractText(fullTime(this.model.created_at()))
    }, this.humanTime = humanTime(this.model.created_at())) : null), this.messageBody()), this.avatarNode(author));
  }
  legacyContent(author) {
    return m("div", {
      className: "ChatMessage-row"
    }, this.avatarNode(author), m("div", {
      className: "message-block"
    }, m("div", {
      className: "toolbar"
    }, m("a", {
      className: "name",
      onclick: this.modelEvent.bind(this, 'insertMention')
    }, extractText(username(author)) + ': '), m("div", {
      className: "labels"
    }, this.labels.map(label => label.condition() ? label.component() : null)), m("div", {
      className: "right"
    }, this.model.id() ? [this.model.isDeletedForever ? null : this.editDropDown(), m("a", {
      className: "timestamp",
      title: extractText(fullTime(this.model.created_at()))
    }, this.humanTime = humanTime(this.model.created_at()))] : this.model.isTimedOut ? this.editDropDownTimedOut() : null)), this.messageBody()));
  }
  avatarNode(author) {
    if (author) {
      return m(Link, {
        className: "avatar-wrapper",
        href: app.route.user(author)
      }, m("span", null, m(Avatar, {
        user: author,
        className: "avatar"
      })));
    }
    return m("div", {
      className: "avatar-wrapper"
    }, m("span", null, m(Avatar, {
      user: author,
      className: "avatar"
    })));
  }
  messageBody() {
    const body = this.displayContent != null ? this.displayContent : this.model.message();
    return m("div", {
      className: "message"
    }, this.model.is_censored() ? m("div", {
      className: "censored actualMessage",
      title: app.translator.trans('flatrate-live-chat.forum.chat.message.censored')
    }, body) : m("div", {
      className: "actualMessage",
      oncreate: this.onContentWrapperCreated.bind(this),
      onupdate: this.onContentWrapperUpdated.bind(this)
    }, body));
  }
  isOwnMessage() {
    const author = this.model.user?.();
    const actor = app.session.user;
    if (!author || !actor) return false;
    return String(author.id()) === String(actor.id());
  }
  isMessagesV2() {
    return this.attrs.presentationVersion === 2;
  }
  view(vnode) {
    return m("div", {
      className: classList({
        'message-wrapper': true,
        'message-wrapper--own': this.isOwnMessage(),
        'message-wrapper--grouped': !!this.attrs.grouped,
        hidden: this.model.deleted_by(),
        editing: this.model.isEditing,
        deleted: !this.isVisible()
      }),
      "data-id": this.model.id()
    }, this.model ? this.content() : null);
  }
  initLabels() {
    this.labelBind(() => this.model.edited_at(), () => m("div", {
      class: "icon",
      title: extractText(app.translator.trans('core.forum.post.edited_tooltip', {
        user: this.model.user(),
        ago: humanTime(this.model.edited_at())
      }))
    }, m("i", {
      class: "fas fa-pencil-alt"
    })));
    this.labelBind(() => this.model.deleted_by(), () => m("div", {
      class: "icon"
    }, m("i", {
      class: "fas fa-trash-alt"
    }), ' ', m("span", null, `(${app.translator.trans('flatrate-live-chat.forum.chat.message.deleted' + (this.model.isDeletedForever ? '_forever' : ''))}`, ' ', username(this.model.deleted_by()), ')')));
    this.labelBind(() => this.model.isTimedOut, () => m("div", {
      class: "icon",
      style: "color: #ff4063"
    }, m("i", {
      class: "fas fa-exclamation-circle"
    })));
  }
  labelBind(condition, component) {
    this.labels.push({
      condition: condition,
      component: component
    });
  }
  editDropDown() {
    const items = new ItemList();
    if (app.chat.getPermissions().edit && this.model.user() && this.model.user() == app.session.user) {
      items.add('dropdownEditStart', m(Button, {
        onclick: this.modelEvent.bind(this, 'dropdownEditStart'),
        icon: "fas fa-pencil-alt",
        disabled: this.model.deleted_by() || this.model.isEditing
      }, app.translator.trans('core.forum.post_controls.edit_button')));
    }
    items.add('separator', m(Separator, null));
    if (this.model.chat().role() || app.chat.getPermissions().delete && this.model.user() == app.session.user) {
      if (this.model.deleted_by()) {
        items.add('dropdownRestore', m(Button, {
          onclick: this.modelEvent.bind(this, 'dropdownRestore'),
          icon: "fas fa-reply",
          disabled: !app.chat.getPermissions().moderate.delete && this.model.deleted_by() != app.session.user
        }, app.translator.trans('core.forum.post_controls.restore_button')));
      } else {
        items.add('dropdownHide', m(Button, {
          onclick: this.modelEvent.bind(this, 'dropdownHide'),
          icon: "fas fa-trash-alt",
          disabled: this.model.isEditing
        }, app.translator.trans('core.forum.post_controls.delete_button')));
      }
    }
    if (this.model.chat().role() && (this.model.deleted_by() || app.chat.totalHidden() >= 3)) {
      items.add('dropdownDelete', m(Button, {
        onclick: this.modelEvent.bind(this, 'dropdownDelete'),
        icon: "fas fa-trash-alt",
        disabled: !app.chat.getPermissions().delete
      }, app.translator.trans('core.forum.post_controls.delete_forever_button')));
    }
    return Object.keys(items.items).length <= 1 ? null : m("div", {
      className: "edit"
    }, m(Dropdown, {
      buttonClassName: "Button Button--icon Button--flat",
      menuClassName: "Dropdown-menu Dropdown-menu--top Dropdown-menu--bottom Dropdown-menu--left Dropdown-menu--right",
      icon: "fas fa-ellipsis-h"
    }, items.toArray()));
  }
  editDropDownTimedOut() {
    return m("div", {
      className: "edit"
    }, m(Dropdown, {
      buttonClassName: "Button Button--icon Button--flat",
      menuClassName: "Dropdown-menu--top Dropdown-menu--bottom Dropdown-menu--left Dropdown-menu--right",
      icon: "fas fa-ellipsis-h"
    }, m(Button, {
      onclick: this.modelEvent.bind(this, 'dropdownDelete'),
      icon: "fas fa-trash-alt"
    }, app.translator.trans('flatrate-live-chat.forum.chat.message.actions.hide')), m(Button, {
      onclick: this.modelEvent.bind(this, 'dropdownResend'),
      icon: "fas fa-reply"
    }, app.translator.trans('flatrate-live-chat.forum.chat.message.actions.resend'))));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.messageWrapper = vnode.dom;
    this.pollInterval = setInterval(() => {
      this.renderMessage();
    }, 100);
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearInterval(this.pollInterval);
  }
  onContentWrapperUpdated(vnode) {
    super.onupdate(vnode);
    this.renderMessage(vnode.dom);
  }
  onContentWrapperCreated(vnode) {
    super.oncreate(vnode);
    this.renderMessage(vnode.dom);
  }
  renderMessage() {
    if (this.model.isNeedToFlash) {
      app.chat.flashItem($(this.messageWrapper));
      this.model.isNeedToFlash = false;
    }
    const body = this.displayContent != null ? this.displayContent : this.model.message();
    if (body !== this.oldContent) {
      this.oldContent = body;
      app.chat.renderChatMessage(this.model, body);
    }
  }
  isVisible() {
    if (this.model.chat() != app.chat.getCurrentChat()) return false;
    if (this.model.isDeletedForever) return false;
    if (this.model.deleted_by() && !(this.model.chat().role() || this.model.user() == app.session.user)) return false;
    return true;
  }
}
flarum.reg.add('flatrate-live-chat', 'forum/components/ChatMessage', ChatMessage);